import axios, { AxiosRequestConfig, AxiosResponse, Method } from 'axios';

import { resetAuthLocalStorage, verifyAccessToken } from '../auth';

export type RequestConfig = Partial<
  Omit<AxiosRequestConfig, 'url' | 'method' | 'data' | 'params'>
> & {
  headers?: Record<string, string>;
};

export class ApiClient {
  private combineURL(url: string): string {
    const serverPath = process.env.REACT_APP_SERVER_PATH || '';

    // Remove leading /api if present
    if (url.startsWith('/api')) {
      url = url.replace(/^\/api/, '');
    }

    // Remove leading slash
    if (url.startsWith('/')) {
      url = url.substring(1);
    }

    // If URL already has the server path, return as is
    if (url.startsWith(serverPath)) {
      return new URL(url).href;
    }

    // Otherwise, combine with server path
    return new URL(`/${url}`, serverPath).href;
  }

  private getAuthHeader() {
    const token = localStorage.getItem('access');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  private async request<T = unknown>(
    method: Method,
    url: string,
    data: unknown = undefined,
    params: Record<string, unknown> | undefined = undefined,
    config: RequestConfig = {},
  ): Promise<AxiosResponse<T>> {
    // Create a copy of params to avoid mutating the original
    const requestParams = params ? { ...params } : {};
    const requestData = data
      ? { ...(data as Record<string, unknown>) }
      : undefined;
    const requestConfig: AxiosRequestConfig = {
      ...config,
      method,
      url: this.combineURL(url),
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
        ...config.headers,
      },
      params: requestParams,
      data: requestData,
    };

    try {
      return await this.sendRequest<T>(requestConfig);
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { status: number; data: unknown };
        name: string;
        message: string;
        stack?: string;
      };

      // Handle 401 Unauthorized - try to refresh token and retry
      if (axiosError.response?.status === 401) {
        try {
          await verifyAccessToken();
          // Update auth header with new token and retry
          requestConfig.headers = {
            ...requestConfig.headers,
            ...this.getAuthHeader(),
          };
          return this.sendRequest<T>(requestConfig);
        } catch (refreshError) {
          // If refresh token fails, log out the user
          resetAuthLocalStorage();
          window.location.href = '/login';
          throw new Error('Session expired. Please log in again.');
        }
      }

      throw error;
    }
  }

  private async sendRequest<T>(
    config: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      return await axios.request<T>(config);
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { status: number; data: unknown };
        name: string;
        message: string;
        stack?: string;
      };

      if (axiosError.response?.status === 503) {
        this.handleMaintenanceMode();
      }

      this.logError(config, {
        response: axiosError.response,
        name: axiosError.name,
        message: axiosError.message,
        stack: axiosError.stack,
      });

      throw error;
    }
  }

  private handleMaintenanceMode() {
    resetAuthLocalStorage();
    localStorage.setItem('maintenanceEnabled', 'true');

    // Only redirect if not already on maintenance page
    if (!window.location.pathname.includes('maintenance')) {
      // Store the current location to return to after maintenance
      sessionStorage.setItem(
        'returnTo',
        window.location.pathname + window.location.search,
      );
      window.location.href = '/maintenance';
    }
  }

  private logError(
    config: AxiosRequestConfig,
    error: {
      response?: unknown;
      name: string;
      message: string;
      stack?: string;
    },
  ) {
    // Don't log 401 errors to avoid cluttering the console
    const status = (error.response as { status?: number })?.status;
    if (status === 401) return;

    // Using console.error is acceptable for error logging
    // eslint-disable-next-line no-console
    console.error({
      timestamp: new Date().toISOString(),
      message: 'API Request Failed',
      url: config.url,
      method: config.method,
      params: config.params,
      data: config.data,
      headers: {
        ...config.headers,
        // Don't log the full auth token
        Authorization: config.headers?.Authorization
          ? 'Bearer [REDACTED]'
          : undefined,
      },
      error: {
        status,
        response: error.response,
        name: error.name,
        message: error.message,
        stack: process.env.NODE_ENV === 'development' ? error.stack : undefined,
      },
    });
  }

  public async get<T = unknown>(
    url: string,
    params?: Record<string, unknown>,
    config?: RequestConfig,
  ) {
    const response = await this.request<T>(
      'GET',
      url,
      undefined,
      params,
      config,
    );
    return response.data;
  }

  public async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: RequestConfig,
  ) {
    const response = await this.request<T>(
      'POST',
      url,
      data,
      undefined,
      config,
    );
    return response.data;
  }

  public async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: RequestConfig,
  ) {
    const response = await this.request<T>('PUT', url, data, undefined, config);
    return response.data;
  }

  public async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: RequestConfig,
  ) {
    const response = await this.request<T>(
      'PATCH',
      url,
      data,
      undefined,
      config,
    );
    return response.data;
  }

  public async delete<T = void>(url: string, config?: RequestConfig) {
    const response = await this.request<T>(
      'DELETE',
      url,
      undefined,
      undefined,
      config,
    );
    return response.data;
  }
}

export const apiClient = new ApiClient();
